import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  FileSpreadsheet, Download, Upload, CheckCircle, XCircle, Clock,
  Building2, Users, Megaphone, DollarSign, Scale, UserCheck, 
  Settings, Monitor, PenTool, FileDown, FileUp, Eye, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENT_CONFIG = {
  admin: { name: 'Admin Department', icon: Building2, color: 'bg-slate-600' },
  dm_channel: { name: 'DM Channel', icon: Users, color: 'bg-emerald-600' },
  marketing: { name: 'Marketing', icon: Megaphone, color: 'bg-blue-600' },
  finance: { name: 'Finance', icon: DollarSign, color: 'bg-green-600' },
  legal: { name: 'Legal', icon: Scale, color: 'bg-purple-600' },
  hr: { name: 'Human Resources', icon: UserCheck, color: 'bg-pink-600' },
  operations: { name: 'Operations', icon: Settings, color: 'bg-orange-600' },
  it: { name: 'IT Department', icon: Monitor, color: 'bg-cyan-600' },
  architect: { name: 'Architect', icon: PenTool, color: 'bg-indigo-600' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'text-green-600 bg-green-50' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-600 bg-red-50' },
};

const ExcelReports = () => {
  const { user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('download');
  const [selectedDept, setSelectedDept] = useState(user?.department || 'admin');
  const [selectedFormat, setSelectedFormat] = useState('xlsx');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'department_head';

  useEffect(() => {
    if (activeTab === 'submissions') {
      fetchSubmissions();
    }
  }, [activeTab, filterDept, filterStatus]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      let url = `${API}/excel/submissions?limit=100`;
      if (filterDept) url += `&department=${filterDept}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(res.data.submissions || []);
    } catch (err) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (department, format) => {
    try {
      const token = localStorage.getItem('crm_token');
      const today = new Date().toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API}/excel/report/${department}?format=${format}&date=${today}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${department}_daily_report_${today}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${DEPARTMENT_CONFIG[department]?.name} report`);
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const handleDownloadTemplate = async (department, format) => {
    try {
      const token = localStorage.getItem('crm_token');
      
      const response = await axios.get(
        `${API}/excel/template/${department}?format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${department}_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${DEPARTMENT_CONFIG[department]?.name} template`);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload only .xlsx or .xls files');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API}/excel/submit?department=${selectedDept}&report_type=daily`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Report submitted successfully!');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setUploading(false);
    }
  };

  const handleReviewSubmission = async (submissionId, status) => {
    try {
      const token = localStorage.getItem('crm_token');
      await axios.put(
        `${API}/excel/submissions/${submissionId}/review?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Submission ${status}`);
      fetchSubmissions();
    } catch (err) {
      toast.error('Failed to update submission');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="excel-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-emerald-600" />
            Excel Reports
          </h1>
          <p className="text-stone-500 mt-1">Download daily reports and submit Excel data</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-stone-200">
        <button
          onClick={() => setActiveTab('download')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'download' 
              ? 'text-emerald-600 border-b-2 border-emerald-600' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
          data-testid="tab-download"
        >
          <Download className="w-4 h-4 inline mr-2" />
          Download Reports
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'upload' 
              ? 'text-emerald-600 border-b-2 border-emerald-600' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
          data-testid="tab-upload"
        >
          <Upload className="w-4 h-4 inline mr-2" />
          Submit Report
        </button>
        <button
          onClick={() => setActiveTab('submissions')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'submissions' 
              ? 'text-emerald-600 border-b-2 border-emerald-600' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
          data-testid="tab-submissions"
        >
          <Eye className="w-4 h-4 inline mr-2" />
          View Submissions
        </button>
      </div>

      {/* Download Reports Tab */}
      {activeTab === 'download' && (
        <div className="space-y-6">
          {/* Format Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Format</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value="xlsx"
                    checked={selectedFormat === 'xlsx'}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    className="w-4 h-4 text-emerald-600"
                  />
                  <span className="font-medium">.xlsx</span>
                  <span className="text-sm text-stone-500">(Modern Excel)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value="xls"
                    checked={selectedFormat === 'xls'}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    className="w-4 h-4 text-emerald-600"
                  />
                  <span className="font-medium">.xls</span>
                  <span className="text-sm text-stone-500">(Legacy Excel)</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Department Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(DEPARTMENT_CONFIG).map(([deptId, config]) => {
              const Icon = config.icon;
              return (
                <Card key={deptId} className="hover:shadow-lg transition-shadow" data-testid={`dept-card-${deptId}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg ${config.color}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-stone-800 mb-1">{config.name}</h3>
                    <p className="text-sm text-stone-500 mb-4">Daily report for {config.name}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDownloadReport(deptId, selectedFormat)}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`download-report-${deptId}`}
                      >
                        <FileDown className="w-4 h-4 mr-1" />
                        Report
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadTemplate(deptId, selectedFormat)}
                        data-testid={`download-template-${deptId}`}
                      >
                        Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUp className="w-5 h-5 text-emerald-600" />
                Submit Daily Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Department Selection */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Select Department
                </label>
                <select
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="select-department"
                >
                  {Object.entries(DEPARTMENT_CONFIG).map(([deptId, config]) => (
                    <option key={deptId} value={deptId}>{config.name}</option>
                  ))}
                </select>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Upload Excel File
                </label>
                <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="excel-upload"
                    data-testid="file-input"
                  />
                  <label htmlFor="excel-upload" className="cursor-pointer">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-stone-400 mb-3" />
                    <p className="text-stone-600 font-medium">
                      {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-sm text-stone-500 mt-1">
                      Supports .xlsx and .xls files
                    </p>
                  </label>
                </div>
              </div>

              {/* Quick Template Download */}
              <div className="bg-stone-50 rounded-lg p-4">
                <p className="text-sm text-stone-600 mb-2">
                  Need a template? Download the submission template for your department:
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadTemplate(selectedDept, 'xlsx')}
                  data-testid="download-dept-template"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download {DEPARTMENT_CONFIG[selectedDept]?.name} Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <Filter className="w-5 h-5 text-stone-500" />
                <select
                  value={filterDept}
                  onChange={(e) => setFilterDept(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  data-testid="filter-department"
                >
                  <option value="">All Departments</option>
                  {Object.entries(DEPARTMENT_CONFIG).map(([deptId, config]) => (
                    <option key={deptId} value={deptId}>{config.name}</option>
                  ))}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  data-testid="filter-status"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Submissions Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                </div>
              ) : submissions.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  No submissions found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="submissions-table">
                    <thead className="bg-stone-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">File</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Submitted</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Status</th>
                        {isAdmin && (
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {submissions.map((sub) => {
                        const StatusIcon = STATUS_CONFIG[sub.status]?.icon || Clock;
                        return (
                          <tr key={sub.id} className="hover:bg-stone-50" data-testid={`submission-row-${sub.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                                <div>
                                  <p className="font-medium text-stone-800 text-sm">{sub.fileName}</p>
                                  <p className="text-xs text-stone-500">{(sub.fileSize / 1024).toFixed(1)} KB</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-stone-700">
                                {DEPARTMENT_CONFIG[sub.department]?.name || sub.department}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-stone-100 text-stone-700 text-xs rounded-full">
                                {sub.reportType}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-600">
                              {new Date(sub.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[sub.status]?.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                {STATUS_CONFIG[sub.status]?.label || sub.status}
                              </span>
                            </td>
                            {isAdmin && (
                              <td className="px-4 py-3">
                                {sub.status === 'pending' && (
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-600 border-green-600 hover:bg-green-50"
                                      onClick={() => handleReviewSubmission(sub.id, 'approved')}
                                      data-testid={`approve-${sub.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-600 hover:bg-red-50"
                                      onClick={() => handleReviewSubmission(sub.id, 'rejected')}
                                      data-testid={`reject-${sub.id}`}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ExcelReports;
